import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Flex,
  Checkbox,
  Space,
  InputNumber,
} from "antd";
import MarkdownEditor from "@uiw/react-markdown-editor";
import { useForm } from "antd/es/form/Form";
import { Exam } from "../redux/apiSlice/examSlice";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { getCategories } from "../redux/apiActions/categoryAction";
import { getProducts } from "../redux/apiActions/productAction";
import { MinusCircleIcon } from "../assets";
import Select from "../components/Select";
import { DatePicker } from "antd";
import QuestionForm from "./QuestionForm";
import QuestionFormData from "./QuestionFormData";
import { CloseOutlined } from "@ant-design/icons";
import { createExam, updateExam } from "../redux/apiActions/examActions";
import ProductManager from "../components/ProductManager";
import moment from "moment";
import dayjs from "dayjs";
import { getExamState, getProductState } from "../redux/store/getState";
import handleApiError from "../utils/handleError";
import FileUpload from "../components/FileUpload";
import { notification } from "antd";

type Props = {
  record?: Exam;
  popup: any;
};

function questionTypeRank(type: unknown): number {
  const t = typeof type === "string" ? type.trim().toLowerCase() : "";
  if (t === "mcq") return 0;
  if (t === "flag" || t === "practical") return 1;
  return 2;
}

function createdAtMs(q: any, fallback: number): number {
  const raw = q?.created_at ?? q?.createdAt;
  if (typeof raw === "number") return raw > 1e12 ? raw : raw * 1000;
  if (typeof raw === "string") {
    const t = Date.parse(raw);
    if (!isNaN(t)) return t;
  }
  // New questions added in the form have no created_at yet
  return Number.MAX_SAFE_INTEGER - 1_000_000 + fallback;
}

function sortQuestionsByType<T>(questions: T[] | undefined | null): T[] {
  if (!Array.isArray(questions)) return [];
  return [...questions]
    .map((q, i) => ({ q, i, t: createdAtMs(q, i) }))
    .sort((a, b) => {
      const rankDiff =
        questionTypeRank((a.q as any)?.type) -
        questionTypeRank((b.q as any)?.type);
      if (rankDiff !== 0) return rankDiff;
      if (a.t !== b.t) return a.t - b.t;
      return a.i - b.i;
    })
    .map((x) => x.q);
}

const formLayout1 = { xs: 24, sm: 9, md: 9, lg: 9 };
const formLayout12 = { xs: 24, sm: 6, md: 6, lg: 6 };

const ExamForm: React.FC<Props> = ({ record, popup }: Props) => {
  const [form] = useForm();
  const dispatch = useAppDispatch();

  const { loading } = useAppSelector(getExamState);
  const { products, loading: productLoading } = useAppSelector(getProductState);

  const [showScoreInput, setshowScoreInput] = useState(false);
  const [showLabsInputs, setShowLabsInputs] = useState(false);
  const [activeFormKey, setActiveFormKey] = useState<null | number>(null);
  const [questions, setQuestions] = useState(true);
  const [slotError, setSlotError] = useState<any>(null);

  useEffect(() => {
    form.resetFields();
    setActiveFormKey(null);
    setQuestions(true);
    setshowScoreInput(false);
    setShowLabsInputs(false);

    if (record) {
      const r = record as Record<string, unknown>;
      const values: any = {
        ...record,
        lab1_ami_id: r.lab1_ami_id ?? "",
        lab1_ip: r.lab1_ip ?? "",
        lab2_ami_id: r.lab2_ami_id ?? "",
        lab2_ip: r.lab2_ip ?? "",
        use_xvda: r.use_xvda ?? false,
      };
      if (Array.isArray((record as any).questions)) {
        values.questions = sortQuestionsByType((record as any).questions);
      }
      form.setFieldsValue(values);
      setshowScoreInput(Boolean(record.report));
      setShowLabsInputs(Boolean(record.lab_available));
    }
  }, [form, record]);

  function setSlotErrorMessage(message: string | null) {
    setSlotError(message);
    setTimeout(() => {
      setSlotError(null);
    }, 5000);
  }

  useEffect(() => {
    dispatch(getCategories());
    dispatch(getProducts());
  }, [dispatch]);

  const onFinish = async (values: Exam) => {
    const payload: any = { ...values };
    if (Array.isArray((values as any).questions)) {
      payload.questions = sortQuestionsByType((values as any).questions);
    }

    // Defense in depth: required rules only validate fields currently rendered
    // (questions in display-mode hide Type/Category Form.Items). Check the
    // underlying form values so we don't post a half-filled question that
    // would blow up the fk_questions_category constraint with a 500.
    const questionsArr: any[] = Array.isArray(payload.questions)
      ? payload.questions
      : [];
    for (let i = 0; i < questionsArr.length; i++) {
      const q = questionsArr[i] || {};
      const missing: string[] = [];
      if (!String(q.description || "").trim()) missing.push("description");
      if (!String(q.type || "").trim()) missing.push("type");
      if (!String(q.answer || "").trim()) missing.push("answer");
      if (!String(q?.category?.id || "").trim()) missing.push("category");
      if (missing.length) {
        notification.error({
          message: `Question ${i + 1} is incomplete`,
          description: `Missing: ${missing.join(", ")}`,
        });
        setQuestions(false);
        setActiveFormKey(i);
        return;
      }

      // An MCQ answer is graded by matching it against the option texts, so an
      // answer that is not one of the options can never be scored as correct.
      if (q.type === "mcq") {
        const normalize = (value: any) =>
          String(value ?? "")
            .trim()
            .toLowerCase();
        const optionTexts = (Array.isArray(q.option) ? q.option : [])
          .map(normalize)
          .filter(Boolean);

        if (!optionTexts.includes(normalize(q.answer))) {
          notification.error({
            message: `Question ${i + 1} has no correct option`,
            description:
              "The correct option must be one of the options typed for this question.",
          });
          setQuestions(false);
          setActiveFormKey(i);
          return;
        }
      }
    }

    let res;
    try {
      if (record) {
        res = await dispatch(updateExam({ ...payload, id: record.id }));
      } else {
        res = await dispatch(createExam(payload));
      }
      if (res.payload.status === "ok") {
        popup.close();
        if (!record) {
          form.resetFields();
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // The questions modal is collapsed with `visibility: hidden`, so a field error
  // raised in there is invisible and "Update Exam" just looks dead. Reopen the
  // modal on the offending question instead of failing silently.
  const onFinishFailed = ({ errorFields }: any) => {
    const inQuestions = (errorFields || []).find(
      (entry: any) => entry?.name?.[0] === "questions"
    );
    if (!inQuestions) return;

    const index = Number(inQuestions.name[1]);
    notification.error({
      message: `Question ${index + 1} is incomplete`,
      description: inQuestions.errors?.[0] ?? "Fix the highlighted field.",
    });
    setQuestions(false);
    setActiveFormKey(index);
  };

  const handleActivateForm = (key: number) => {
    setActiveFormKey(key);
  };

  async function handleAddMore(fieldsLength: number, add: any) {
    try {
      await form.validateFields(["questions"], { recursive: true });
      add();
      handleActivateForm(fieldsLength);
    } catch (error) {}
  }

  function handleAddQuestions() {
    const currentFields = form.getFieldValue("questions") || [];

    if (currentFields.length === 0) {
      form.setFieldsValue({ questions: [{ key: 0 }] });
      setActiveFormKey(0);
    }

    setQuestions(false);
  }
  function handleViewQuestions() {
    setQuestions(false);
    setActiveFormKey(null);
  }

  async function handleSubmitQuestion() {
    try {
      await form.validateFields(["questions"], {
        recursive: true,
      });
      setQuestions(true);
    } catch (error) {}
  }

  const onCheckboxChange = (e: { target: { checked: boolean } }) => {
    setshowScoreInput(e.target.checked);
  };
  const onLabsCheckboxChange = (e: { target: { checked: boolean } }) => {
    setShowLabsInputs(e.target.checked);
  };

  function handleSlotSelect(momentDateAndTime: any, add: any) {
    if (momentDateAndTime) {
      const newSlotUnix = momentDateAndTime.format("DD/MM/YYYY hh:mm A");
      const existingSlots = form
        .getFieldValue("slots")
        ?.map((slot: number) => moment.unix(slot).format("DD/MM/YYYY hh:mm A"));

      if (existingSlots?.includes(newSlotUnix)) {
        return setSlotErrorMessage("This slot has already been selected");
      }
      setSlotErrorMessage(null);
      add(momentDateAndTime.unix());
    }
  }

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{ report: false, lab_available: false, use_xvda: false }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      className="exam_form"
      // requiredMark={false}
    >
      <Form.Item
        label="Exam Name"
        name="name"
        rules={[{ required: true, message: "Exam name is required" }]}
      >
        <Input />
      </Form.Item>
      <div data-color-mode="dark">
        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: "Description is required" }]}
          data-color-mode="dark"
        >
          <MarkdownEditor height="150px" />
        </Form.Item>
      </div>
      <div data-color-mode="dark">
        <Form.Item
          label="Instructions"
          name="instruction"
          rules={[{ required: true, message: "Instructions is required" }]}
          data-color-mode="dark"
        >
          <MarkdownEditor height="150px" />
        </Form.Item>
      </div>

      <Space style={{ marginBottom: 15 }} size="large">
        <Button type="primary" onClick={handleAddQuestions}>
          Add Questions
        </Button>
        {form.getFieldValue("questions")?.length ? (
          <Button type="primary" onClick={handleViewQuestions}>
            View Questions
          </Button>
        ) : null}
      </Space>
      <div
        style={{ visibility: questions ? "hidden" : "visible" }}
        className="question_modal_container"
      >
        <Form.Item
          layout="vertical"
          hidden={questions}
          className="question_modal"
        >
          <Form.List name="questions">
            {(fields, { add, remove }) => {
              const sortedFields = [...fields].sort((a, b) => {
                const aVal = form.getFieldValue(["questions", a.name]) || {};
                const bVal = form.getFieldValue(["questions", b.name]) || {};
                const rankDiff =
                  questionTypeRank(aVal.type) - questionTypeRank(bVal.type);
                if (rankDiff !== 0) return rankDiff;
                const at = createdAtMs(aVal, a.name);
                const bt = createdAtMs(bVal, b.name);
                if (at !== bt) return at - bt;
                return a.name - b.name;
              });
              return (
              <>
                <p
                  className="questions_close_icon"
                  onClick={() => setQuestions(true)}
                >
                  <CloseOutlined />
                </p>
                {sortedFields.map((field) => (
                  <div style={{ marginBottom: 40 }} key={field.key}>
                    {field.name !== activeFormKey || activeFormKey === null ? (
                      <QuestionFormData
                        field={field}
                        form={form}
                        onDelete={remove}
                        onEdit={() => handleActivateForm(field.name)}
                      />
                    ) : (
                      <QuestionForm
                        field={field}
                        onDelete={remove}
                        form={form}
                      />
                    )}
                  </div>
                ))}

                <Form.Item>
                  <Space size="large" style={{ marginTop: 20 }}>
                    <Button type="primary" onClick={handleSubmitQuestion}>
                      Submit
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => handleAddMore(fields.length, add)}
                    >
                      Add More
                    </Button>
                  </Space>
                </Form.Item>
              </>
              );
            }}
          </Form.List>
        </Form.Item>
      </div>

      <Row gutter={15}>
        <Col {...formLayout1}>
          <Form.Item
            label="Duration"
            name="duration"
            rules={[{ required: true, message: "Duration is required" }]}
          >
            <InputNumber
              placeholder="Minutes"
              className="full-width"
              type="number"
            />
          </Form.Item>
        </Col>
        <Col {...formLayout1}>
          <Select
            options={products}
            loading={productLoading.getProduct}
            formItemProps={{
              label: "Product",
              name: ["product", "id"],
              rules: [{ required: true, message: "Product is required" }],
            }}
            dropdownRender={<ProductManager />}
          />
        </Col>
        <Col {...formLayout12}>
          <Form.Item
            label="Slot Limit"
            name="slot_limit"
            rules={[{ required: true, message: "Slot Limit is required" }]}
          >
            <InputNumber type="number" className="full-width" />
          </Form.Item>
        </Col>
      </Row>

      <Form.List name="slots">
        {(fields, { add, remove }) => (
          <>
            <Form.Item
              label="Select Slot"
              validateStatus={slotError ? "error" : ""}
              help={slotError ? slotError : ""}
            >
              <DatePicker
                minDate={dayjs()}
                showTime
                format="DD/MM/YYYY hh:mm A"
                onChange={(value) => handleSlotSelect(value, add)}
                placeholder="DD/MM/YYYY"
              />
            </Form.Item>

            <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
              {fields.map((field) => (
                <Col key={field.key}>
                  <div className="pill_secondary">
                    {moment(
                      form.getFieldValue(["slots", field.name]) * 1000
                    ).format("DD/MM/YYYY hh:mm A")}
                    <MinusCircleIcon onClick={() => remove(field.name)} />
                  </div>
                  <Input
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10); // Convert input to integer
                      const values = form.getFieldValue("slots");
                      values[field.name] = isNaN(value) ? "" : value;
                      form.setFieldsValue({ slots: values });
                    }}
                    style={{ display: "none" }}
                  />
                </Col>
              ))}
            </Row>
          </>
        )}
      </Form.List>

      <Row gutter={10}>
        <Col>
          <Form.Item
            label="Expire By"
            name="expire_by"
            rules={[{ required: true, message: "Expire By is required" }]}
            getValueProps={(value) => ({
              value: value ? dayjs(value * 1000) : null,
            })}
            getValueFromEvent={(value) => (value ? value.unix() : null)}
          >
            <DatePicker
              placeholder=""
              minDate={dayjs().add(1, "day")}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item name="report" valuePropName="checked" label=" ">
            <Checkbox onChange={onCheckboxChange}>Report</Checkbox>
          </Form.Item>
        </Col>

        {showScoreInput ? (
          <Col>
            <Form.Item
              label="Score"
              name="report_score"
              rules={[{ required: true, message: "Score is required" }]}
            >
              <InputNumber stringMode={false} type="number" />
            </Form.Item>
          </Col>
        ) : null}
      </Row>

      <Form.Item
        label="Enter Amount"
        name="amount"
        rules={[{ required: true, message: "Amount is required" }]}
      >
        <InputNumber suffix="USD" style={{ width: "30%" }} />
      </Form.Item>

      <FileUpload
        form={form}
        btnText="Upload Logo"
        text="No Logo Uploaded"
        file_type="logo"
      />

      {/* lab_available */}
      <Form.Item name="lab_available" valuePropName="checked" label=" ">
        <Checkbox onChange={onLabsCheckboxChange}>Lab Available</Checkbox>
      </Form.Item>

      {/* use_xvda - always mounted so it's included in form submit (conditionally rendered fields can be omitted) */}
      <Form.Item
        name="use_xvda"
        valuePropName="checked"
        label=" "
        hidden={!showLabsInputs}
      >
        <Checkbox>Use xvda</Checkbox>
      </Form.Item>

      {showLabsInputs && (
        <>
          <Row gutter={10}>
            <Col flex={1}>
              <Form.Item label="Lab 1 AMI ID" name="lab1_ami_id">
                <Input />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item label="Lab 1 IP" name="lab1_ip">
                <Input />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item label="Lab 2 AMI ID" name="lab2_ami_id">
                <Input />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item label="Lab 2 IP" name="lab2_ip">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      <Flex justify={"center"}>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading.createExam || loading.updateExam}
            disabled={loading.createExam || loading.updateExam}
          >
            {record ? "Update" : "Create"} Exam
          </Button>
        </Form.Item>
      </Flex>
    </Form>
  );
};

export default ExamForm;
